import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './index.css';
import {showToast} from "../../utils/messageUtil";

function MapSelect() {
    const navigate = useNavigate();
    const { locationType } = useParams();
    
    // 存储出发地和目的地信息
    const [fromValue, setFromValue] = useState('');
    const [toValue, setToValue] = useState('');
    const [fromLocation, setFromLocation] = useState(null);
    const [toLocation, setToLocation] = useState(null);
    const [previousPath, setPreviousPath] = useState(''); // 记录之前的页面路径
    
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const placeSearchRef = useRef(null);
    const aMapRef = useRef(null); // 存储AMap对象，方便在其他函数中使用

    // 处理返回按钮
    const handleBack = () => {
        navigate(-1);
    };

    const [isSearching, setIsSearching] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const fromAutoCompleteRef = useRef(null);
    const toAutoCompleteRef = useRef(null);

    // 在组件加载时获取并存储之前的页面路径
    useEffect(() => {
        // 从sessionStorage中获取之前的页面路径
        const storedPreviousPath = sessionStorage.getItem('previousPath');
        if (storedPreviousPath) {
            setPreviousPath(storedPreviousPath);
            console.log('从sessionStorage读取之前的页面路径:', storedPreviousPath);
        } else {
            // 如果没有存储的路径，使用document.referrer
            const referrer = document.referrer;
            console.log('从document.referrer获取之前的页面路径:', referrer);
            // 提取路径部分
            if (referrer) {
                try {
                    const url = new URL(referrer);
                    setPreviousPath(url.pathname);
                    console.log('提取路径部分:', url.pathname);
                } catch (e) {
                    console.error('解析referrer失败:', e);
                }
            }
        }
    }, []);

    useEffect(() => {
        window._AMapSecurityConfig = {
            securityJsCode: "a8fc2f3553e82bb29efb33154dbecd0d"
        };
        // 直接通过script标签加载高德地图API（去掉securityJsCode测试）
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = `https://webapi.amap.com/maps?v=2.0&key=ab7068ca37ab9cc5d2efc66efb691223`;
        script.onload = () => {
            console.log('高德地图API加载完成');
            
            // 确保全局AMap对象可用
            const AMap = window.AMap;
            if (!AMap) {
                console.error('AMap对象未找到');
                setError('AMap对象未找到');
                setLoading(false);
                return;
            }
            aMapRef.current = AMap; // 存储AMap对象到ref中
            
            // 使用高德地图的IP定位服务获取用户当前位置（通过IP地址）
            AMap.plugin('AMap.CitySearch', () => {
                const citySearch = new AMap.CitySearch();
                
                // 使用IP定位获取用户当前城市
                citySearch.getLocalCity((status, result) => {
                    console.log('IP定位结果:', status, result);
                    
                    let center;
                    let zoom = 12;
                    
                    if (status === 'complete' && result.city && result.bounds) {
                        // 定位成功，使用城市中心作为地图中心
                        center = result.bounds.getCenter();
                        // console.log('获取到用户当前城市:', result.city, '中心坐标:', center);
                    } else {
                        // 定位失败，使用默认位置（北京）
                        console.error('IP定位失败:', result);
                        center = [116.4074, 39.9042];
                    }
                    
                    // 初始化地图
                    try {
                        mapRef.current = new AMap.Map(mapContainerRef.current, {
                            resizeEnable: true,
                            center: center,
                            zoom: zoom
                        });
                        
                        // 在地图上添加当前位置标记
                        new AMap.Marker({
                            position: center,
                            map: mapRef.current,
                            title: '当前位置'
                        });
                    } catch (e) {
                        console.error('地图初始化失败:', e);
                        setError('地图初始化失败: ' + e.message);
                        setLoading(false);
                        return;
                    }
                });
            });

            // 使用官方demo的方式加载插件
            AMap.plugin(['AMap.PlaceSearch', 'AMap.AutoComplete'], () => {
                console.log('PlaceSearch和AutoComplete插件加载完成');
                // 构造地点查询类
                const placeSearch = new AMap.PlaceSearch({
                    city: '全国', // 设置搜索范围为全国
                    citylimit: false, // 允许跨城市搜索
                    pageSize: 20, // 设置每页结果数量
                    pageIndex: 1 // 设置当前页码
                });
                placeSearchRef.current = placeSearch;

                // 初始化出发地AutoComplete
                const fromAutoComplete = new AMap.AutoComplete({
                    input: 'from-tipinput', // 与出发地input的id对应
                    city: '全国', // 设置搜索范围为全国
                    output: 'poi' // 设置返回结果类型为poi
                });
                fromAutoCompleteRef.current = fromAutoComplete;

                // 注册出发地select事件
                fromAutoComplete.on('select', (e) => {
                    console.log('用户选择了出发地:', e.poi);
                    setFromValue(e.poi.name);
                    setFromLocation({
                        name: e.poi.name,
                        address: e.poi.district || e.poi.address || '未知地址',
                        lat: e.poi.location?.lat || 0,
                        lng: e.poi.location?.lng || 0
                    });
                    
                    // 搜索并定位到出发地
                    placeSearch.search(e.poi.name, (status, result) => {
                        console.log('出发地搜索结果:', status, result);
                        if (status === 'complete' && result.info === 'OK' && result.poiList?.pois?.length > 0) {
                            const poi = result.poiList.pois[0];
                            mapRef.current.setCenter([poi.location.lng, poi.location.lat]);
                            mapRef.current.setZoom(15);
                        }
                    });
                });

                // 初始化目的地AutoComplete
                const toAutoComplete = new AMap.AutoComplete({
                    input: 'to-tipinput', // 与目的地input的id对应
                    city: '全国', // 设置搜索范围为全国
                    output: 'poi' // 设置返回结果类型为poi
                });
                toAutoCompleteRef.current = toAutoComplete;

                // 注册目的地select事件
                toAutoComplete.on('select', (e) => {
                    console.log('用户选择了目的地:', e.poi);
                    setToValue(e.poi.name);
                    setToLocation({
                        name: e.poi.name,
                        address: e.poi.district || e.poi.address || '未知地址',
                        lat: e.poi.location?.lat || 0,
                        lng: e.poi.location?.lng || 0
                    });
                    console.log('更新目的地状态:', {
                        name: e.poi.name,
                        address: e.poi.district || e.poi.address || '未知地址',
                        lat: e.poi.location?.lat || 0,
                        lng: e.poi.location?.lng || 0
                    });
                    
                    // 搜索并定位到目的地
                    placeSearch.search(e.poi.name, (status, result) => {
                        console.log('目的地搜索结果:', status, result);
                        if (status === 'complete' && result.info === 'OK' && result.poiList?.pois?.length > 0) {
                            const poi = result.poiList.pois[0];
                            mapRef.current.setCenter([poi.location.lng, poi.location.lat]);
                            mapRef.current.setZoom(15);
                        }
                    });
                });

            });
            
            setLoading(false);
        };
        
        script.onerror = (error) => {
            console.error('高德地图API加载失败:', error);
            setError('地图API加载失败');
            setLoading(false);
        };
        
        // 添加script标签到head
        document.head.appendChild(script);

        return () => {
            // 组件卸载时清理资源
            if (mapRef.current) {
                mapRef.current.destroy();
            }
            // 移除script标签
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, []);

    // 处理提交按钮点击
    const handleSubmit = () => {
        console.log('===== handleSubmit开始执行 =====');
        console.log('locationType:', locationType);
        console.log('fromLocation:', fromLocation);
        console.log('toLocation:', toLocation);
        
        // 检查locationType是否有效
        if (!locationType) {
            console.error('locationType未定义');
            showToast('参数错误，请重新选择', 'error');
            return;
        }
        
        // 存储选择的出发地和目的地信息
        try {
            // 存储出发地信息
            if (fromLocation || fromValue) {
                const fromName = fromLocation ? fromLocation.name : fromValue;
                sessionStorage.setItem('fromLocation', fromName);
                console.log('存储出发地名称到单独键:', fromName);
            }
            
            // 存储目的地信息
            if (toLocation || toValue) {
                const toName = toLocation ? toLocation.name : toValue;
                sessionStorage.setItem('toLocation', toName);
                console.log('存储目的地名称到单独键:', toName);
            }
            
            // 根据locationType决定存储到selectedLocation键中的信息
            if (locationType === 'from') {
                console.log('===== 处理出发地选择 =====');
                // 只需要选择出发地
                if (!fromLocation && !fromValue) {
                    console.error('fromLocation和fromValue均未定义');
                    showToast('请选择出发地', 'error');
                    return;
                }
                
                const locationInfo = {
                    type: 'from',
                    location: fromLocation || {
                        name: fromValue,
                        address: '未知地址',
                        lat: 0,
                        lng: 0
                    }
                };
                sessionStorage.setItem('selectedLocation', JSON.stringify(locationInfo));
                console.log('存储出发地信息:', locationInfo);
            } else if (locationType === 'to') {
                console.log('===== 处理目的地选择 =====');
                // 只需要选择目的地
                if (!toLocation && !toValue) {
                    console.error('toLocation和toValue均未定义');
                    showToast('请选择目的地', 'error');
                    return;
                }
                
                const locationInfo = {
                    type: 'to',
                    location: toLocation || {
                        name: toValue,
                        address: '未知地址',
                        lat: 0,
                        lng: 0
                    }
                };
                sessionStorage.setItem('selectedLocation', JSON.stringify(locationInfo));
                console.log('存储目的地信息:', locationInfo);
            } else {
                console.log('===== 处理同时选择出发地和目的地 =====');
                // 同时选择出发地和目的地
                if ((!fromLocation && !fromValue) || (!toLocation && !toValue)) {
                    console.error('fromLocation/fromValue或toLocation/toValue未定义');
                    showToast('请选择出发地和目的地', 'error');
                    return;
                }
                
                const locationsInfo = {
                    from: fromLocation || {
                        name: fromValue,
                        address: '未知地址',
                        lat: 0,
                        lng: 0
                    },
                    to: toLocation || {
                        name: toValue,
                        address: '未知地址',
                        lat: 0,
                        lng: 0
                    }
                };
                sessionStorage.setItem('selectedLocations', JSON.stringify(locationsInfo));
                console.log('存储出发地和目的地信息:', locationsInfo);
            }
        } catch (error) {
            console.error('存储地点信息失败:', error);
        }
        
        // 打印当前sessionStorage中的所有信息，以便于调试
        console.log('===== sessionStorage中的所有信息 =====');
        console.log('fromLocation:', sessionStorage.getItem('fromLocation'));
        console.log('toLocation:', sessionStorage.getItem('toLocation'));
        console.log('selectedLocation:', sessionStorage.getItem('selectedLocation'));
        console.log('selectedTimeInfo:', sessionStorage.getItem('selectedTimeInfo'));
        console.log('selectedVehicle:', sessionStorage.getItem('selectedVehicle'));
        
        // 获取sessionStorage中存储的车辆信息
        const selectedVehicle = sessionStorage.getItem('selectedVehicle');
        // console.log('从sessionStorage读取车辆信息:', selectedVehicle);
        
        // 跳转到之前的页面或默认页面
        console.log('===== 准备跳转 =====');
        console.log('navigate函数:', navigate);
        console.log('之前的页面路径:', previousPath);
        try {
            // 根据之前的页面路径决定跳转目标
            if (previousPath === '/reserve') {
                // 如果之前是从预约页面来的，返回到预约页面
                console.log('执行navigate(\'/reserve\')');
                navigate('/reserve');
                console.log('跳转到预约页面成功');
            } else if (previousPath === '/dispatch') {
                // 如果之前是从派车页面来的，返回到派车页面
                console.log('执行navigate(\'/dispatch\')');
                navigate('/dispatch');
                console.log('跳转到派车页面成功');
            } else if (previousPath === '/time-select') {
                // 如果之前是从时间选择页面来的，返回到时间选择页面
                if (selectedVehicle) {
                    try {
                        const vehicleData = JSON.parse(selectedVehicle);
                        if (vehicleData.plateNumber) {
                            const vehicleStr = JSON.stringify(vehicleData);
                            const encodedVehicle = encodeURIComponent(vehicleStr);
                            const encodedDate = encodeURIComponent(vehicleData.selectedDate || new Date().toISOString().split('T')[0]);
                            console.log('执行navigate(\'/time-select?vehicle=...\')');
                            navigate(`/time-select?vehicle=${encodedVehicle}&date=${encodedDate}`);
                            console.log('跳转到带参数的时间选择页面成功');
                            return;
                        }
                    } catch (parseError) {
                        console.error('解析车辆信息失败:', parseError);
                    }
                }
                console.log('执行navigate(\'/time-select\')');
                navigate('/time-select');
                console.log('跳转到时间选择页面成功');
            } else {
                // 如果没有之前的页面信息，使用默认行为
                if (selectedVehicle) {
                    try {
                        const vehicleData = JSON.parse(selectedVehicle);
                        if (vehicleData.plateNumber) {
                            const vehicleStr = JSON.stringify(vehicleData);
                            const encodedVehicle = encodeURIComponent(vehicleStr);
                            const encodedDate = encodeURIComponent(vehicleData.selectedDate || new Date().toISOString().split('T')[0]);
                            console.log('执行navigate(\'/time-select?vehicle=...\')');
                            navigate(`/time-select?vehicle=${encodedVehicle}&date=${encodedDate}`);
                            console.log('跳转到带参数的时间选择页面成功');
                            return;
                        }
                    } catch (parseError) {
                        console.error('解析车辆信息失败:', parseError);
                    }
                }
                console.log('执行navigate(\'/time-select\')');
                navigate('/time-select');
                console.log('跳转到时间选择页面成功');
            }
        } catch (error) {
            console.error('跳转失败:', error);
        }
        console.log('===== handleSubmit执行结束 =====');
    };

    return (
        <div className='map-select'>
            <div className='navbar'>
                <div className='navbar-left' onClick={handleBack}>
                    <span className='back-icon'>‹</span>
                </div>
                <div className='navbar-title'>
                    {locationType === 'from' ? '选择出发地' : locationType === 'to' ? '选择目的地' : '选择出发地和目的地'}
                </div>
                <div className='navbar-right'></div>
            </div>

            <div className='location-inputs-container'>
                {/* 出发地输入框 */}
                <div className='location-input-wrapper'>
                    <div className='location-label'>出发地</div>
                        <input
                    type="text" 
                    placeholder="搜索出发地" 
                    value={fromValue} 
                    onChange={(e) => {
                        setFromValue(e.target.value);
                        // 当用户输入时，清除之前选择的地点信息
                        setFromLocation(null);
                    }}
                    onBlur={(e) => {
                        // 当输入框失去焦点时，如果用户输入了内容但没有选择AutoComplete建议，使用输入的内容作为地点信息
                        if (e.target.value && !fromLocation) {
                            console.log('用户直接输入了出发地:', e.target.value);
                            setFromLocation({
                                name: e.target.value,
                                address: '未知地址',
                                lat: 0,
                                lng: 0
                            });
                            // 搜索并定位到出发地
                            if (placeSearchRef.current) {
                                placeSearchRef.current.search(e.target.value, (status, result) => {
                                    console.log('出发地搜索结果:', status, result);
                                    if (status === 'complete' && result.info === 'OK' && result.poiList?.pois?.length > 0) {
                                        const poi = result.poiList.pois[0];
                                        if (mapRef.current) {
                                            mapRef.current.setCenter([poi.location.lng, poi.location.lat]);
                                            mapRef.current.setZoom(15);
                                        }
                                    }
                                });
                            }
                        }
                    }}
                    className="search-input"
                    id="from-tipinput" // 添加id，用于AutoComplete识别
                />
                </div>

                {/* 目的地输入框 */}
                <div className='location-input-wrapper'>
                    <div className='location-label'>目的地</div>
                        <input
                            type="text" 
                            placeholder="搜索目的地" 
                            value={toValue} 
                            onChange={(e) => {
                                setToValue(e.target.value);
                                // 当用户输入时，清除之前选择的地点信息
                                setToLocation(null);
                            }}
                            onBlur={(e) => {
                                // 当输入框失去焦点时，如果用户输入了内容但没有选择AutoComplete建议，使用输入的内容作为地点信息
                                if (e.target.value && !toLocation) {
                                    console.log('用户直接输入了目的地:', e.target.value);
                                    setToLocation({
                                        name: e.target.value,
                                        address: '未知地址',
                                        lat: 0,
                                        lng: 0
                                    });
                                    // 搜索并定位到目的地
                                    if (placeSearchRef.current) {
                                        placeSearchRef.current.search(e.target.value, (status, result) => {
                                            console.log('目的地搜索结果:', status, result);
                                            if (status === 'complete' && result.info === 'OK' && result.poiList?.pois?.length > 0) {
                                                const poi = result.poiList.pois[0];
                                                if (mapRef.current) {
                                                    mapRef.current.setCenter([poi.location.lng, poi.location.lat]);
                                                    mapRef.current.setZoom(15);
                                                }
                                            }
                                        });
                                    }
                                }
                            }}
                            className="search-input"
                            id="to-tipinput" // 添加id，用于AutoComplete识别
                        />
                </div>
            </div>

            {/* 地图显示 */}
            <div ref={mapContainerRef} className='map-display'></div>
            
            {/* 提交按钮 */}
            <div className='submit-button-container'>
                <button 
                    type="button" 
                    className="submit-button"
                    onClick={handleSubmit}
                >
                    确认选择
                </button>
            </div>
        </div>
    );
}

export default MapSelect;